'use client';

import { useCallback, useState } from 'react';

import {
  type DefaultValues,
  type FieldValues,
  type Resolver,
  useForm,
  useWatch,
} from 'react-hook-form';
import type { ZodType } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';

export type DynamicFormContext = {
  security?: {
    honeypotName?: string;
    honeypot?: string;
  };
};

export function useDynamicForm<T extends FieldValues>({
  schema,
  defaultValues,
  onSubmit,
  suppressSuccess = false,
}: {
  schema: ZodType<T>;
  defaultValues: DefaultValues<T>;
  onSubmit?: (values: T, context?: DynamicFormContext) => void | Promise<void>;
  suppressSuccess?: boolean;
}) {
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<T>({
    resolver: zodResolver(schema as any) as Resolver<T>,
    defaultValues,
    mode: 'onBlur',
  });

  const watchedValues = useWatch({ control }) as T;

  const onSubmitHandler = async (values: T) => {
    setSubmitError(null);
    try {
      await onSubmit?.(values);
      if (!suppressSuccess) {
        setSuccess(true);
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    }
  };

  const resetFormState = useCallback(() => {
    setSuccess(false);
    setSubmitError(null);
    reset(defaultValues);
  }, [defaultValues, reset]);

  return {
    control,
    errors,
    isSubmitting,
    watchedValues,
    success,
    submitError,
    handleFormSubmit: handleSubmit(onSubmitHandler),
    resetFormState,
  };
}
